/**
 * Profiles resource writes: the client interface for the `profiles.*` oRPC
 * endpoints that mutate a silo identity.
 *
 * `updateProfileAsync`, `updateProfileManageAsync`, and `deleteProfileAsync`
 * are all plain `Datastore`-taking functions rather than hooks because their
 * call sites fire them from callbacks (onboarding submit, admin override
 * toggles, the title selector, the account-delete modal), not from React
 * render — mirroring `me.ts`'s `fetchMyRolesAsync`. Each reaches the
 * instance's API client and silo key off the passed `Datastore`.
 * `updateProfileAsync` is the self-only update path (`customName`,
 * `photoHidden`, `answersVisible`, `consentAccepted` — the server resolves its
 * own `profileKey` from the session, so callers only pass the fields they want
 * to change) and returns the updated `DetailedUserProfile` the server echoes
 * back. `profiles.manage.update` is the admin-only path (every field it
 * writes is an admin override — `title`, `isAffiliated`, `overrideName`,
 * `overridePhotoUrl`); the server owns the field allowlist and the mapping onto
 * stored fields in both cases. `deleteProfileAsync` is the self-only account
 * deletion path (`profiles.delete`) — it removes the caller's profile and all
 * their content, so the caller signs out and navigates to login afterward.
 *
 * Every one of them invalidates the affected profile's `useProfile` read on
 * success (`invalidateProfile`, from the read-side `./profile.ts`) — the reads
 * these writes affect used to ride on the datastore's instance-data refresh,
 * which no longer covers them.
 */
import type { InferContractRouterInputs } from '@orpc/contract';
import type { DetailedUserProfile, profilesContract } from '@repo/shared/orpc/procedures/profiles';
import type { profilesManageContract } from '@repo/shared/orpc/procedures/profiles-manage';
import type { Datastore } from 'util/datastore';

import { invalidateProfile } from './profile';
import { siloParamsOf } from './silo-params';

type UpdateProfileFields = Omit<InferContractRouterInputs<typeof profilesContract.update>, 'siloKey' | 'profileKey'>;

export async function updateProfileAsync(datastore: Datastore, fields: UpdateProfileFields): Promise<DetailedUserProfile> {
    const profileKey = datastore.getPersonaKey();
    if (!profileKey) {
        throw new Error('Cannot update profile without a signed-in user');
    }
    const updated = await datastore.getApiClient().profiles.update({ ...siloParamsOf(datastore), profileKey, ...fields });
    invalidateProfile(datastore, profileKey);
    return updated;
}

type UpdateProfileManageFields = Omit<InferContractRouterInputs<typeof profilesManageContract.update>, 'siloKey'>;

export async function updateProfileManageAsync(datastore: Datastore, fields: UpdateProfileManageFields): Promise<DetailedUserProfile> {
    const updated = await datastore.getApiClient().profiles.manage.update({ ...siloParamsOf(datastore), ...fields });
    invalidateProfile(datastore, fields.profileKey);
    return updated;
}

export async function deleteProfileAsync(datastore: Datastore): Promise<void> {
    const profileKey = datastore.getPersonaKey();
    if (!profileKey) {
        throw new Error('Cannot delete profile without a signed-in user');
    }
    await datastore.getApiClient().profiles.delete({ ...siloParamsOf(datastore), profileKey });
    invalidateProfile(datastore, profileKey);
}
